import type { Provider } from '@supabase/supabase-js';

import { z } from 'zod';

const providers: z.ZodType<Provider> = getProviders();

const AuthConfigSchema = z.object({
  captchaTokenSiteKey: z
    .string({
      description: 'The reCAPTCHA site key.',
    })
    .optional(),
  displayTermsCheckbox: z
    .boolean({
      description: 'Whether to display the terms checkbox during sign-up.',
    })
    .optional(),
  enableIdentityLinking: z
    .boolean({
      description: 'Allow linking and unlinking of auth identities.',
    })
    .optional()
    .default(false),
  providers: z.object({
    password: z.boolean({
      description: 'Enable password authentication.',
    }),
    magicLink: z.boolean({
      description: 'Enable magic link authentication.',
    }),
    otp: z.boolean({
      description: 'Enable one-time password authentication.',
    }),
    phone: z.boolean({
      description: 'Enable phone authentication.',
    }),
    oAuth: providers.array(),
  }),
});

const authConfig = AuthConfigSchema.parse({
  // NB: This is a public key, so it's safe to expose.
  // Copy the value from the Supabase Dashboard.
  captchaTokenSiteKey: process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY,

  // whether to display the terms checkbox during sign-up
  // Defaults to true so terms acceptance is enforced unless explicitly opted out.
  displayTermsCheckbox:
    process.env.NEXT_PUBLIC_DISPLAY_TERMS_AND_CONDITIONS_CHECKBOX !== 'false',

  // whether to enable identity linking:
  // This needs to be enabled in the Supabase Console as well for it to work.
  enableIdentityLinking:
    process.env.NEXT_PUBLIC_AUTH_IDENTITY_LINKING === 'true',

  // NB: Enable the providers below in the Supabase Console
  // in your production project
  providers: {
    password: process.env.NEXT_PUBLIC_AUTH_PASSWORD === 'true',
    magicLink: process.env.NEXT_PUBLIC_AUTH_MAGIC_LINK === 'true',
    otp: process.env.NEXT_PUBLIC_AUTH_OTP === 'true',
    phone: process.env.NEXT_PUBLIC_AUTH_PHONE === 'true',
    oAuth: getEnabledOAuthProviders(),
  },
} satisfies z.infer<typeof AuthConfigSchema>);

export default authConfig;

function getEnabledOAuthProviders(): Provider[] {
  const providers: Provider[] = [];
  
  // Add Google if enabled
  if (process.env.NEXT_PUBLIC_AUTH_GOOGLE === 'true') {
    providers.push('google');
  }
  
  // Add other OAuth providers as needed
  // if (process.env.NEXT_PUBLIC_AUTH_GITHUB === 'true') {
  //   providers.push('github');
  // }
  
  return providers;
}

function getProviders() {
  return z.enum([
    'apple',
    'azure',
    'bitbucket',
    'discord',
    'facebook',
    'figma',
    'github',
    'gitlab',
    'google',
    'kakao',
    'keycloak',
    'linkedin',
    'linkedin_oidc',
    'notion',
    'slack',
    'spotify',
    'twitch',
    'twitter',
    'workos',
    'zoom',
    'fly',
  ]);
}
